import { useEffect, useRef, useState } from "react";

// Tempo da animação de saída (styles/animations.css).
export const EXIT_TRANSITION_MS = 170;

/**
 * Mantém um elemento montado durante a animação de saída.
 *
 * `open` verdadeiro -> `{ render: true, closing: false }`.
 * `open` falso      -> `closing: true` por EXIT_TRANSITION_MS e depois
 *                      `render: false` (o chamador desmonta).
 * Com `prefers-reduced-motion`, desmonta na hora.
 */
export default function useExitTransition(open, duration = EXIT_TRANSITION_MS) {
  const [render, setRender] = useState(Boolean(open));
  const [closing, setClosing] = useState(false);
  const timerRef = useRef(null);
  const renderRef = useRef(render);
  renderRef.current = render;

  useEffect(() => {
    if (open) {
      clearTimeout(timerRef.current);
      setClosing(false);
      setRender(true);
      return undefined;
    }
    if (!renderRef.current) return undefined;
    const reduceMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      setRender(false);
      return undefined;
    }
    setClosing(true);
    timerRef.current = setTimeout(() => {
      setRender(false);
      setClosing(false);
    }, duration);
    return () => clearTimeout(timerRef.current);
  }, [open, duration]);

  return { render: Boolean(open) || render, closing: closing && !open };
}
